/**
 * Conversations, the frame every place sits in: on a computer and an iPad the rail with the place beside it (three
 * panes, two upright); on a phone the Conversations tab, and each place pushed over it. It holds what the places
 * share: the rail's data, the Start a circle sheet and the last place opened.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router";
import type { HostTalkResponse } from "../../shared/talk";
import { type Layout, useLayout } from "../components/season/layout";
import { UNREAD_CHANGED } from "../components/phone/unread";
import { api } from "../lib/api";
import type { CircleSeed, TalkNavState } from "./handoff";
import { type CircleAnswer, type CirclePeople, NewCircleSheet } from "./NewCircleSheet";
import { type Place, placeOf, roomHome, talkPath } from "./places";
import { Rail, type RailTab } from "./Rail";
import { firstRoom } from "./rooms";
import { type Phase, useRemote } from "./useTalk";

export interface TalkContext {
  rail: HostTalkResponse | null;
  phase: Phase;
  lay: Layout;
  reloadRail: () => void;
  /** Opens Start a circle for a room's group, for the people a search found, or for the people who picked one choice of a poll. */
  startCircle: (webinarId: string | null, seed?: CircleSeed, people?: CirclePeople, answer?: CircleAnswer) => void;
}

export const useTalkContext = () => useOutletContext<TalkContext>();

const RAIL_POLL_MS = 60_000;
/** The last place opened in each tab, so a tab pressed again comes back to it. */
const lastPlace: { rooms: string | null; direct: string | null } = { rooms: null, direct: null };
export const rememberedHome = () => lastPlace.rooms ?? lastPlace.direct;

const tabOfPlace = (p: Place): RailTab | null => (p.kind === "direct" ? "direct" : p.kind === "none" || p.kind === "search" ? null : "rooms");

export function TalkLayout() {
  const lay = useLayout();
  const phone = lay === "phone";
  const { pathname, search, state } = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const place = placeOf(pathname);
  const rail = useRemote("talk:rail", api.talk.rail);
  const [sheet, setSheet] = useState<{ webinarId: string | null; seed?: CircleSeed; people?: CirclePeople; answer?: CircleAnswer } | null>(null);
  const [tab, setTab] = useState<RailTab>(tabOfPlace(place) ?? "rooms");

  const reloadRail = useCallback(() => void rail.reload(), [rail.reload]);

  // Why a body class: the toast lives outside this tree, and anywhere in the columns it sits on words.
  useEffect(() => {
    document.body.classList.add("talk-on");
    return () => document.body.classList.remove("talk-on");
  }, []);

  useEffect(() => {
    const t = tabOfPlace(place);
    if (t) setTab(t);
    if (t) lastPlace[t] = pathname;
  }, [place.kind, pathname]);

  useEffect(() => {
    const s = state as TalkNavState | null;
    if (!s?.circle && !s?.circleRoom) return;
    setSheet(s.circle ? { webinarId: s.circle.webinarId, seed: s.circle } : { webinarId: s.circleRoom ?? null });
    navigate(pathname + search, { replace: true, state: null });
  }, [state, pathname, search, navigate]);

  useEffect(() => {
    const t = window.setInterval(() => document.visibilityState === "visible" && reloadRail(), RAIL_POLL_MS);
    window.addEventListener(UNREAD_CHANGED, reloadRail);
    return () => {
      window.clearInterval(t);
      window.removeEventListener(UNREAD_CHANGED, reloadRail);
    };
  }, [reloadRail]);

  const startCircle = useCallback((webinarId: string | null, seed?: CircleSeed, people?: CirclePeople, answer?: CircleAnswer) => setSheet({ webinarId, seed, people, answer }), []);
  const ctx = useMemo<TalkContext>(() => ({ rail: rail.data, phase: rail.phase, lay, reloadRail, startCircle }), [rail.data, rail.phase, lay, reloadRail, startCircle]);

  const onTab = (t: RailTab) => {
    setTab(t);
    const firstOne = firstRoom(rail.data?.rooms ?? []);
    const first = t === "rooms" ? (firstOne ? roomHome(firstOne) : null) : rail.data?.direct[0] ? `${talkPath}/direct/${encodeURIComponent(rail.data.direct[0].contactId)}` : null;
    const to = lastPlace[t] ?? first;
    if (to) navigate(to);
  };

  const sheetEl = sheet ? (
    <NewCircleSheet
      rooms={rail.data?.rooms ?? []}
      webinarId={sheet.webinarId}
      seed={sheet.seed}
      people={sheet.people}
      answer={sheet.answer}
      phone={phone}
      onClose={() => setSheet(null)}
      onMade={reloadRail}
    />
  ) : null;

  if (phone) {
    return (
      <div className="hp-root">
        <Outlet context={ctx} />
        {sheetEl}
      </div>
    );
  }
  return (
    <div className="talk" data-lay={lay}>
      <div className="cx">
        <Rail rail={rail.data} place={place} tab={tab} onTab={onTab} q={place.kind === "search" ? (params.get("q") ?? "") : ""} wide={lay === "computer"} onCircle={(w) => startCircle(w)} reload={reloadRail} />
        <Outlet context={ctx} />
      </div>
      {sheetEl}
    </div>
  );
}
